import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import {
  AbsenceRequest,
  IAbsenceRequest,
  AbsenceRequestState as AbsenceState
} from "@/models/absenceRequest-model";
import { absenceRequestService } from "@/services/AbsenceRequest.service";
import { StoreCache } from "../storeCache";
import { IAbsenceRequestsState } from "./types";
import { IIDAStore } from "../types";

export const getInitialState = (): IAbsenceRequestsState => {
  return {
    searching: false,
    absenceRequests: {},
    absenceRequestsKeys: {},
    storeCache: new StoreCache(),
    loading: false,
    error: null
  };
};

const namespaced = true;

export const getters: GetterTree<IAbsenceRequestsState, IIDAStore> = {
  getAbsenceRequests: (state) => state.absenceRequests,
  getLoading: (state) => state.loading,
  getError: (state) => state.error,
  getPendingRequests: (state) =>
    Object.values(state.absenceRequests).filter(
      (request) => request.state === AbsenceState.Pending
    ),
  getApprovedRequests: (state) =>
    Object.values(state.absenceRequests).filter(
      (request) => request.state === AbsenceState.Approved
    ),
  getRejectedRequests: (state) =>
    Object.values(state.absenceRequests).filter(
      (request) => request.state === AbsenceState.Rejected
    )
};

export const actions: ActionTree<IAbsenceRequestsState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  async fetchAbsenceRequests(
    { commit },
    { fromDate, toDate }: { fromDate: Date; toDate: Date; forceUpdate?: boolean } = {
      fromDate: new Date(),
      toDate: new Date()
    }
  ) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const requests = await absenceRequestService.getAbsenceRequests(fromDate, toDate);
      commit("SET_ABSENCE_REQUESTS", requests);
      return requests;
    } catch (error) {
      commit("SET_ERROR", error instanceof Error ? error.message : "An error occurred");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async createAbsenceRequest({ commit }, request: IAbsenceRequest) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const newRequest = await absenceRequestService.createAbsenceRequest(request);
      commit("ADD_ABSENCE_REQUEST", newRequest);
      return newRequest;
    } catch (error) {
      commit("SET_ERROR", error instanceof Error ? error.message : "An error occurred");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async updateAbsenceRequest(
    { commit },
    { externalId, request }: { externalId: string; request: IAbsenceRequest }
  ) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const updatedRequest = await absenceRequestService.updateAbsenceRequest(externalId, request);
      commit("UPDATE_ABSENCE_REQUEST", updatedRequest);
      return updatedRequest;
    } catch (error) {
      commit("SET_ERROR", error instanceof Error ? error.message : "An error occurred");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async deleteAbsenceRequest({ commit }, externalId: string) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      await absenceRequestService.deleteAbsenceRequest(externalId);
      commit("REMOVE_ABSENCE_REQUEST", externalId);
    } catch (error) {
      commit("SET_ERROR", error instanceof Error ? error.message : "An error occurred");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async approveAbsenceRequest(
    { commit },
    { externalId, request }: { externalId: string; request: IAbsenceRequest }
  ) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const updatedRequest = await absenceRequestService.approveAbsenceRequest(externalId, request);
      commit("UPDATE_ABSENCE_REQUEST", updatedRequest);
      return updatedRequest;
    } catch (error) {
      commit("SET_ERROR", error instanceof Error ? error.message : "An error occurred");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async rejectAbsenceRequest({ commit }, externalId: string) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const updatedRequest = await absenceRequestService.rejectAbsenceRequest(externalId);
      commit("UPDATE_ABSENCE_REQUEST", updatedRequest);
      return updatedRequest;
    } catch (error) {
      commit("SET_ERROR", error instanceof Error ? error.message : "An error occurred");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  }
};

export const mutations: MutationTree<IAbsenceRequestsState> = {
  reset(state: IAbsenceRequestsState) {
    Object.assign(state, getInitialState());
  },
  SET_ABSENCE_REQUESTS(state: IAbsenceRequestsState, requests: AbsenceRequest[]) {
    state.absenceRequests = requests.reduce((acc, request) => {
      acc[request.external_id] = request;
      return acc;
    }, {} as Record<string, AbsenceRequest>);
  },
  ADD_ABSENCE_REQUEST(state: IAbsenceRequestsState, request: AbsenceRequest) {
    state.absenceRequests[request.external_id] = request;
  },
  UPDATE_ABSENCE_REQUEST(state: IAbsenceRequestsState, updatedRequest: AbsenceRequest) {
    state.absenceRequests[updatedRequest.external_id] = updatedRequest;
  },
  REMOVE_ABSENCE_REQUEST(state: IAbsenceRequestsState, externalId: string) {
    delete state.absenceRequests[externalId];
  },
  SET_LOADING(state: IAbsenceRequestsState, loading: boolean) {
    state.loading = loading;
  },
  SET_ERROR(state: IAbsenceRequestsState, error: string | null) {
    state.error = error;
  }
};

export const absenceRequestsStoreModule: Module<IAbsenceRequestsState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
